import { Injectable } from '@angular/core';
import { PredictionRotaWeek } from '../../classes/models/PredictionRotaWeek';
import { StoreSummary } from '../../classes/models/store/StoreSummary';
import { Authority } from '../../classes/enums/Authority';
import { AuthUser } from '../../auth/AuthUser';

/**
 * This service contains data which can be accessed from any class.
 */
@Injectable({
  providedIn: 'root'
})
export class DataService {

  /** All stores, assigned to the current user. */
  stores: StoreSummary[];
  /** Combined predictions and rotas list. */
  predictionRotaWeeks: PredictionRotaWeek[];

  private readonly SELECTED_STORE_KEY = 'selectedStore';
  private readonly USER_DATA_KEY = 'userData';
  private readonly RANGE_FROM_KEY = 'rangeFrom';
  private readonly RANGE_TO_KEY = 'rangeTo';
  private readonly EDIT_DATE_KEY = 'editDate';

  constructor() {
    this.stores = [];
    this.predictionRotaWeeks = [];
  }

  /**
   * Get authenticated user data from the local storage.
   */
  get userData(): AuthUser {
    return JSON.parse(localStorage.getItem(this.USER_DATA_KEY));
  }

  /**
   * Store authenticated user data to the local storage.
   * @param userData authenticated user data to be stored.
   */
  set userData(userData: AuthUser) {
    localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(userData));
  }

  /**
   * Get selected store from the local storage.
   */
  get selectedStore(): StoreSummary {
    return JSON.parse(localStorage.getItem(this.SELECTED_STORE_KEY));
  }

  /**
   * Store selected store to the local storage.
   * @param selectedStore selected store to be stored.
   */
  set selectedStore(selectedStore: StoreSummary) {
    localStorage.setItem(this.SELECTED_STORE_KEY, JSON.stringify(selectedStore));
  }

  /**
   * Get the first day of predictions/rotas from local storage.
   */
  get rangeFrom(): string {
    return localStorage.getItem(this.RANGE_FROM_KEY);
  }

  /**
   * Store the first day of predictions/rotas.
   * @param rangeFrom the first day of predictions/rotas to be stored.
   */
  set rangeFrom(rangeFrom: string) {
    localStorage.setItem(this.RANGE_FROM_KEY, rangeFrom);
  }

  /**
   * Get the last day of predictions/rotas from local storage.
   */
  get rangeTo(): string {
    return localStorage.getItem(this.RANGE_TO_KEY);
  }

  /**
   * Store the last day of predictions/rotas.
   * @param rangeTo the last day of predictions/rotas to be stored.
   */
  set rangeTo(rangeTo: string) {
    localStorage.setItem(this.RANGE_TO_KEY, rangeTo);
  }

  /**
   * Get the first day, for which the input data can still be edited, from local storage.
   */
  get editDate(): string {
    return localStorage.getItem(this.EDIT_DATE_KEY);
  }

  /**
   * Store edit date.
   * @param date the first day, for which the input data can still be edited.
   */
  set editDate(date: string) {
    localStorage.setItem(this.EDIT_DATE_KEY, date);
  }

  /**
   * Checks if a user has access to do a specific action.
   * @param authority an action to check if a user is allowed to perform.
   */
  userHasAuthority(authority: Authority): boolean {
    const user = JSON.parse(localStorage.getItem(this.USER_DATA_KEY));
    return user.authorities.find($authority => $authority === authority) !== undefined;
  }

  /**
   * Checks if a user has access to do several specific actions.
   * @param authorities actions to check if a user is allowed to perform.
   */
  userHasAuthorities(authorities: Authority[]): boolean {
    const user = JSON.parse(localStorage.getItem(this.USER_DATA_KEY));
    return user.authorities.some(authority => authorities.includes(authority));
  }

  /**
   * Checks if a user can edit data on a specific date.
   * @param date date, on which a user is editing data.
   */
  canEditAtDate(date: string): boolean {
    return date >= this.editDate;
  }
}
